<template>
  <div :id="'carousel' + categoryid" class="carousel slide" v-if="products && products.length">
    <section class="embla">
      <div class="embla__viewport" ref="emblaRef" @mouseenter="stopAutoplay" @mouseleave="startAutoplay">
        <div class="embla__container">
          <div
            class="embla__slide product-item"
            v-for="product in products"
            :key="product.id"
            :data-product-id="product.id"
          >
            <Product
              :product="product"
              :categoryid="categoryid"
              :showaddtocart="showaddtocart"
              :addToCartLabel="addToCartLabel"
              :showprocessorder="showprocessorder"
              :showratingstar="showratingstar"
              :getDefaultChoice="getDefaultChoice"
              :fillProductWithChoice="fillProductWithChoice"
              :isProductListFromParent="isProductListFromParent"
            />
          </div>
        </div>
      </div>
      <div class="embla__controls">
        <div class="embla__buttons">
          <button @click="scrollPrev" class="embla__button embla__button--prev" type="button">
            <svg class="embla__button__svg" viewBox="0 0 532 532">
              <path
                fill="currentColor"
                d="M355.66 11.354c13.793-13.805 36.208-13.805 50.001 0 13.785 13.804 13.785 36.238 0 50.034L201.22 266l204.442 204.61c13.785 13.805 13.785 36.239 0 50.044-13.793 13.796-36.208 13.796-50.002 0a5994246.277 5994246.277 0 0 0-229.332-229.454 35.065 35.065 0 0 1-10.326-25.126c0-9.2 3.393-18.26 10.326-25.2C172.192 194.973 332.731 34.31 355.66 11.354Z"
              ></path>
            </svg>
          </button>

          <button @click="scrollNext" class="embla__button embla__button--next" type="button">
            <svg class="embla__button__svg" viewBox="0 0 532 532">
              <path
                fill="currentColor"
                d="M176.34 520.646c-13.793 13.805-36.208 13.805-50.001 0-13.785-13.804-13.785-36.238 0-50.034L330.78 266 126.34 61.391c-13.785-13.805-13.785-36.239 0-50.044 13.793-13.796 36.208-13.796 50.002 0 22.928 22.947 206.395 206.507 229.332 229.454a35.065 35.065 0 0 1 10.326 25.126c0 9.2-3.393 18.26-10.326 25.2-45.865 45.901-206.404 206.564-229.332 229.52Z"
              ></path>
            </svg>
          </button>
        </div>
        <div class="embla__dots">
          <button type="button" :class="`embla__dot ${currentIndex === index ? 'embla__dot--selected' : ''}`" v-for="(product, index) in products" :key="product.id" @click="scrollTo(index)"></button>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, defineProps, onMounted } from "vue";
import emblaCarouselVue from "embla-carousel-vue";
import Autoplay from 'embla-carousel-autoplay'
import Product from "@/components/productlist/subcomponents/Product.vue";

const props = defineProps({
  products: Array,
  categoryid: String,
  showaddtocart: Boolean,
  addToCartLabel: String,
  showprocessorder: Boolean,
  showratingstar: Boolean,
  getDefaultChoice: Function,
  fillProductWithChoice: Function,
  isProductListFromParent: Boolean
})

const options = { loop: true, align: 'start',};
const [emblaRef, emblaApi] = emblaCarouselVue(options, [Autoplay({stopOnMouseEnter: true, delay: 5000})]);

const currentIndex = ref(0);

function scrollNext() {
  emblaApi?.value.scrollNext();
  currentIndex.value = emblaApi.value.selectedScrollSnap();
  emblaApi?.value.plugins().autoplay.reset();
}

function scrollPrev() {
  emblaApi?.value.scrollPrev();
  currentIndex.value = emblaApi.value.selectedScrollSnap();
  emblaApi?.value.plugins().autoplay.reset();
}

function scrollTo(index) {
  emblaApi?.value.scrollTo(index);
  currentIndex.value = emblaApi.value.selectedScrollSnap();
  emblaApi?.value.plugins().autoplay.reset();
}

function startAutoplay() {
  emblaApi?.value.plugins().autoplay.play();
}

function stopAutoplay() {
  emblaApi?.value.plugins().autoplay.stop();
  emblaApi?.value.plugins().autoplay.reset();
}

onMounted(() => {
  if (emblaApi.value) {
    emblaApi.value.on('autoplay:select', api => {
      currentIndex.value = api.selectedScrollSnap();
    })
  }
})

</script>

<style>
.products-slider .embla {
  --slide-height: 19rem;
  --slide-spacing: 1rem;
  --slide-size: 25%;
}

.products-slider .embla__viewport {
  overflow: hidden;
}

.products-slider .embla__container {
  display: flex;
  touch-action: pan-y pinch-zoom;
  margin-left: calc(var(--slide-spacing) * -1);
}

.products-slider .embla__slide {
  transform: translate3d(0, 0, 0);
  flex: 0 0 var(--slide-size);
  min-width: 0;
  padding-left: var(--slide-spacing);
}

.products-slider .embla__buttons {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.6rem;
  align-items: center;
}

.products-slider .embla__button {
  -webkit-tap-highlight-color: rgba(var(--text-high-contrast-rgb-value), 0.5);
  -webkit-appearance: none;
  appearance: none;
  background-color: transparent;
  touch-action: manipulation;
  display: inline-flex;
  text-decoration: none;
  cursor: pointer;
  border: 0;
  padding: 0;
  margin: 0;
  box-shadow: inset 0 0 0 0.2rem var(--detail-medium-contrast);
  width: 3.6rem;
  height: 3.6rem;
  z-index: 1;
  border-radius: 50%;
  color: rgb(54, 49, 61);
  display: flex;
  align-items: center;
  justify-content: center;
}

.products-slider .embla__button:disabled {
  color: rgb(234, 234, 234);
}

.products-slider .embla__button__svg {
  width: 35%;
  height: 35%;
}

.products-slider .embla__controls {
  display: grid;
  grid-template-columns: auto 1fr;
  justify-content: space-between;
  gap: 1.2rem;
}

.products-slider .embla__dots {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: center;
  margin-right: calc((2.6rem - 1.4rem) / 2 * -1);
}

.products-slider .embla__dot {
  -webkit-tap-highlight-color: rgba(var(--text-high-contrast-rgb-value), 0.5);
  -webkit-appearance: none;
  appearance: none;
  background-color: transparent;
  touch-action: manipulation;
  display: inline-flex;
  text-decoration: none;
  cursor: pointer;
  border: 0;
  padding: 0;
  margin: 0;
  width: 2.6rem;
  height: 2.6rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}

.products-slider .embla__dot:after {
  box-shadow: inset 0 0 0 0.2rem rgb(234, 234, 234);
  width: 1.4rem;
  height: 1.4rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  content: "";
}

.products-slider .embla__dot--selected:after {
  box-shadow: inset 0 0 0 0.2rem rgb(54, 49, 61);
}

.products-slider ol {
  margin: 0;
  padding: 0;
  list-style: none;
}

.products-slider .product-item {
  position: relative;
  text-align: center;
  vertical-align: top;
  margin-bottom: 3.5rem;
}

.products-slider .product-image {
  max-width: 100%;
}

.products-slider .old-price-wrapper {
  display: block;
}

.products-slider .old-price {
  display: inline-block;
  position: relative;
  color: var(--oldprice-color);
  font-weight: 500;
  font-size: 1.1rem;
}

.products-slider .old-price:before {
  content: ' ';
  display: block;
  position: absolute;
  top: 50%;
  left: -5px;
  width: calc(100% + 10px);
  height: 1px;
  background: var(--font-color);
}

.products-slider .product-item .promotion-label-container {
  display: block;
  height: 100px;
  width: 100px;
  text-align: center;
  background: var(--promotag-bg-color);
  color: white;
  line-height: 100px;
  border-radius: 50%;
  position: absolute;
  top: 0;
  left: 0;
  font-size: 32px;
  font-weight: 500;
  rotate: -10deg;
}

.products-slider .price {
  text-rendering: optimizeLegibility;
  color: var(--font-color);
  font-size: 1.5rem;
  font-weight: bold !important;
}

.products-slider .product-item-details {
  margin-top: 1rem;
}

.products-slider .product-item-name {
  display: block;
  word-wrap: break-word;
  -webkit-hyphens: auto;
  -moz-hyphens: auto;
  -ms-hyphens: auto;
  hyphens: auto;
  color: var(--primary-color);
  font-weight: bold !important;
  font-size: 1.15rem;
  overflow: hidden;
  text-transform: uppercase;
}

.products-slider .product-item-info.card  {
  border: 0 !important;
}
.products-slider .product-item-details.card-body {
  padding: 0 !important;
}

.products-slider .product-item-details-button.card-footer {
  border: 0 !important;
  background: initial !important;
}

.products-slider .listing-long-name {
  font-size: 0.95rem;
}

.products-slider .product-item-link {
  text-decoration: none;
}

.products-slider .product-item-link:hover {
  text-decoration: underline;
}

.products-slider .price-container {
  display: block;
  margin-bottom: 1rem;
}

.products-slider .product-item-details .know-more {
  display: inline-block;
  margin: 0.5rem 0;
  font-style: italic;
}

@media (max-width: 992px) {
  .products-slider .embla {
    --slide-size: 33.3333%;
  }
}
@media (max-width: 768px) {
  .products-slider .embla {
    --slide-size: 50%;
  }
}
@media (max-width: 347px) {
  .products-slider .embla {
    --slide-size: 100%;
  }
}
</style>